import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore,
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  onSnapshot,
  runTransaction,
} from "firebase/firestore";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";
import { computeTopPizzaFromOrders, buildProductMaps } from "./topPizzaAggregation.js";

let dbInstance = null;
let authInstance = null;
let appInstance = null;

// Inicializa Firebase con variables de entorno.
export const initFirebase = () => {
  if (dbInstance) return dbInstance;

  const config = {
    apiKey: process.env.FIREBASE_API_KEY,
    authDomain: process.env.FIREBASE_AUTH_DOMAIN,
    projectId: process.env.FIREBASE_PROJECT_ID,
    storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.FIREBASE_APP_ID,
    measurementId: process.env.FIREBASE_MEASUREMENT_ID,
  };

  const hasConfig = [
    config.apiKey,
    config.authDomain,
    config.projectId,
    config.storageBucket,
    config.messagingSenderId,
    config.appId,
  ].every(Boolean);
  if (!hasConfig) {
    console.warn("Firebase no esta configurado en .env.");
    return null;
  }

  appInstance = getApps().length ? getApps()[0] : initializeApp(config);
  dbInstance = getFirestore(appInstance);
  return dbInstance;
};

export const getFirebaseApp = () => {
  const db = initFirebase();
  if (!db) return null;
  return appInstance;
};

export const getAuthClient = () => {
  if (authInstance) return authInstance;
  const db = initFirebase();
  if (!db) return null;
  authInstance = getAuth(appInstance);
  return authInstance;
};

export const signInEmailPassword = async (email, password) => {
  const auth = getAuthClient();
  if (!auth) throw new Error("Firebase no disponible.");
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};

export const signOutUser = async () => {
  const auth = getAuthClient();
  if (!auth) return;
  await signOut(auth);
};

export const onAuthChanged = (callback) => {
  const auth = getAuthClient();
  if (!auth) return () => {};
  return onAuthStateChanged(auth, callback);
};

export const registerWithEmailPassword = async (email, password, displayName) => {
  const auth = getAuthClient();
  if (!auth) throw new Error("Firebase no disponible.");
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName?.trim()) {
    await updateProfile(result.user, { displayName: displayName.trim() });
  }
  return result.user;
};

export const signInWithGooglePopup = async () => {
  const auth = getAuthClient();
  if (!auth) throw new Error("Firebase no disponible.");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(auth, provider);
  return result.user;
};

export const signInAnonymous = async () => {
  const auth = getAuthClient();
  if (!auth) throw new Error("Firebase no disponible.");
  const result = await signInAnonymously(auth);
  return result.user;
};

export const sendPasswordResetEmailToUser = async (email) => {
  const auth = getAuthClient();
  if (!auth) throw new Error("Firebase no disponible.");
  await sendPasswordResetEmail(auth, String(email).trim());
};

/**
 * Estadísticas públicas para el sitio (ej. pizza más vendida en / info).
 * Documento: config/stats — lectura pública; no consulta la colección orders.
 */
export const fetchPublicTopPizzaStats = async () => {
  const db = initFirebase();
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, "config", "stats"));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    if (error?.code === "permission-denied") {
      console.warn(
        "Sin permiso para leer config/stats. Verifica reglas Firestore para config/*."
      );
    } else {
      console.warn("No se pudo leer config/stats:", error?.message);
    }
    return null;
  }
};

// Obtiene la configuración pública del negocio.
export const fetchPublicConfig = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  try {
    const configDoc = await getDoc(doc(db, "config", "public"));
    if (!configDoc.exists()) {
      console.warn("⚠️ Documento config/public no existe en Firestore.");
      console.info("💡 Crea el documento config/public en Firestore Database.");
      return null;
    }
    const data = configDoc.data();
 //console.log("✅ Configuración pública cargada:", data.businessName || "Sin nombre");
    return { id: configDoc.id, ...data };
  } catch (error) {
    // Si es error de permisos, dar mensaje más claro
    if (error.code === "permission-denied") {
      console.error(
        "❌ PERMISOS INSUFICIENTES para leer config/public.\n" +
        "📋 SOLUCIÓN: Despliega las reglas de Firestore:\n" +
        "   1. Ejecuta: firebase deploy --only firestore:rules\n" +
        "   2. O desde Firebase Console: Firestore → Reglas → Publicar\n" +
        "   3. Verifica que la regla incluya: match /config/{configId} { allow read: if true; }"
      );
    } else {
      console.error("❌ Error leyendo config/public:", error.message);
    }
    throw error;
  }
};

// Actualiza la configuración pública del negocio.
export const updatePublicConfig = async (payload) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  try {
    const configRef = doc(db, "config", "public");
    
    // Agregar timestamp de actualización
    const data = {
      ...payload,
      updatedAt: new Date(),
    };

    // Usar setDoc con merge para crear o actualizar
    await setDoc(configRef, data, { merge: true });
    
 //console.log("✅ Configuración pública actualizada:", data.businessName || "Sin nombre");
    return { id: "public", ...data };
  } catch (error) {
    console.error("❌ Error actualizando config/public:", error.message);
    throw error;
  }
};

// Obtiene productos destacados desde Firestore.
// Nota: Requiere índice compuesto en Firestore:
// - destacado (ASC) + disponible (ASC) + updatedAt (DESC)
export const fetchFeaturedProducts = async (limitCount = 6) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  try {
    const productsQuery = query(
      collection(db, "products"),
      where("destacado", "==", true),
      where("disponible", "==", true),
      orderBy("updatedAt", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(productsQuery);
    const products = snapshot.docs.map((docItem) => {
      const product = {
        id: docItem.id,
        ...docItem.data(),
      };
      return normalizeProduct(product);
    });
    
    if (products.length === 0) {
      console.warn(
        "⚠️ No se encontraron productos destacados con la query completa.\n" +
        "💡 Verifica en Firestore que los productos tengan:\n" +
        "   - destacado: true (boolean)\n" +
        "   - disponible: true (boolean)\n" +
        "   - updatedAt: Timestamp"
      );
    } else {
   //console.log(`✅ ${products.length} productos destacados cargados desde Firestore.`);
    }
    
    return products;
  } catch (error) {
    // Si falla por índice faltante, intentar query más simple
    if (error.code === "failed-precondition") {
      console.warn(
        "Índice compuesto faltante. Usando query alternativa sin ordenamiento."
      );
      try {
        const simpleQuery = query(
          collection(db, "products"),
          where("destacado", "==", true),
          where("disponible", "==", true),
          limit(limitCount)
        );
        const snapshot = await getDocs(simpleQuery);
        const products = snapshot.docs.map((docItem) => {
          const product = {
            id: docItem.id,
            ...docItem.data(),
          };
          return normalizeProduct(product);
        });
        // Ordenar manualmente por updatedAt si existe
        const sorted = products.sort((a, b) => {
          const aDate = a.updatedAt?.toDate?.() || new Date(0);
          const bDate = b.updatedAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });
        if (sorted.length === 0) {
          console.warn(
            "⚠️ No se encontraron productos destacados.\n" +
            "💡 Verifica en Firestore que los productos tengan:\n" +
            "   - destacado: true\n" +
            "   - disponible: true"
          );
        } else {
       //console.log(`✅ ${sorted.length} productos destacados cargados (sin índice).`);
        }
        return sorted;
      } catch (fallbackError) {
        console.error("Error en query alternativa:", fallbackError);
        throw fallbackError;
      }
    }
    throw error;
  }
};

// Genera el siguiente número de pedido usando una transacción.
export const getNextOrderNumber = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  const counterRef = doc(db, "counters", "orders");

  try {
    const orderNumber = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      let seq = 1;

      if (counterDoc.exists()) {
        seq = (counterDoc.data().seq || 0) + 1;
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = `${now.getMonth() + 1}`.padStart(2, "0");
      const day = `${now.getDate()}`.padStart(2, "0");
      const dateStr = `${year}${month}${day}`;
      const seqStr = `${seq}`.padStart(4, "0");

      const orderNumber = `PZ-${dateStr}-${seqStr}`;

      transaction.set(counterRef, { seq }, { merge: true });

      return orderNumber;
    });

    return orderNumber;
  } catch (error) {
    console.error("Error generando orderNumber:", error);
    // Fallback: usar timestamp + random si falla la transacción
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, "0");
    const day = `${now.getDate()}`.padStart(2, "0");
    const dateStr = `${year}${month}${day}`;
    const random = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0");
    return `PZ-${dateStr}-${random}`;
  }
};

// Crea pedido en Firestore con orderNumber y devuelve referencia.
export const createOrder = async (orderPayload) => {
  const db = initFirebase();
  if (!db) {
    throw new Error("Firebase no disponible.");
  }

  // Generar orderNumber antes de crear el pedido
  const orderNumber = await getNextOrderNumber();

  const payload = {
    ...orderPayload,
    orderNumber,
    channel: orderPayload?.channel || "WEB",
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, "orders"), payload);
  return { id: docRef.id, orderNumber };
};

export const getUserDoc = async (uid) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDoc(doc(db, "users", uid));
  return snapshot.exists() ? snapshot.data() : null;
};

export const setUserDoc = async (uid, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  await setDoc(doc(db, "users", uid), data, { merge: true });
};

export const updateUserDoc = async (uid, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  await updateDoc(doc(db, "users", uid), data);
};

export const listUsers = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDocs(query(collection(db, "users")));
  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data(),
  }));
};

export const listenOrders = (callback) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const ordersQuery = query(collection(db, "orders"), orderBy("createdAt", "desc"));
  return onSnapshot(ordersQuery, (snapshot) => {
    const orders = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));
    callback(orders);
  });
};

export const updateOrder = async (orderId, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  await updateDoc(doc(db, "orders", orderId), data);
};

// Ã“rdenes confirmadas y pagadas (para uso pÃºblico)
const ORDER_STATUS_CONFIRMED = ["CONFIRMADO", "ENTREGADO"];

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const fetchConfirmedPaidOrders = async ({ fromDate } = {}) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  const baseConstraints = [
    where("status", "in", ORDER_STATUS_CONFIRMED),
    where("paid", "==", true),
  ];

  let snapshot = null;

  if (fromDate) {
    const from = toDateValue(fromDate);
    const dateConstraint = from ? where("createdAt", ">=", from) : null;
    try {
      const q = query(
        collection(db, "orders"),
        ...baseConstraints,
        ...(dateConstraint ? [dateConstraint] : []),
        orderBy("createdAt", "desc")
      );
      snapshot = await getDocs(q);
    } catch (error) {
      if (error?.code !== "failed-precondition") {
        throw error;
      }
      // Fallback sin createdAt/orderBy si falta Ã­ndice compuesto
      const fallbackQuery = query(collection(db, "orders"), ...baseConstraints);
      snapshot = await getDocs(fallbackQuery);
    }
  } else {
    const q = query(collection(db, "orders"), ...baseConstraints);
    snapshot = await getDocs(q);
  }

  const orders = snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data(),
  }));

  if (!fromDate) return orders;

  const from = toDateValue(fromDate);
  if (!from) return orders;

  return orders.filter((order) => {
    const createdAt = toDateValue(order.createdAt);
    return createdAt ? createdAt >= from : false;
  });
};

// PRODUCTOS

// Normaliza un producto para asegurar que tenga todos los campos necesarios
// Ya no agrega presentacionesPermitidas ni precios automáticamente
// Solo mantiene compatibilidad entre campos en inglés y español
const normalizeProduct = (product) => {
  // Asegurar campos de compatibilidad (inglés <-> español)
  if (!product.name && product.nombre) {
    product.name = product.nombre;
  }
  if (!product.nombre && product.name) {
    product.nombre = product.name;
  }
  
  if (!product.category && product.categoria) {
    product.category = product.categoria;
  }
  if (!product.categoria && product.category) {
    product.categoria = product.category;
  }
  
  if (!product.description && product.descripcion) {
    product.description = product.descripcion;
  }
  if (!product.descripcion && product.description) {
    product.descripcion = product.description;
  }
  
  // Compatibilidad de precio: priceBase <-> precioBase <-> price
  if (!product.priceBase && product.precioBase) {
    product.priceBase = product.precioBase;
  }
  if (!product.precioBase && product.priceBase) {
    product.precioBase = product.priceBase;
  }
  if (!product.priceBase && !product.precioBase && product.price) {
    product.priceBase = product.price;
    product.precioBase = product.price;
  }
  
  // Compatibilidad de cantidad: quantity <-> cantidad <-> porciones
  if (!product.quantity && product.cantidad) {
    product.quantity = product.cantidad;
  }
  if (!product.cantidad && product.quantity) {
    product.cantidad = product.quantity;
  }
  if (!product.quantity && !product.cantidad && product.porciones) {
    product.quantity = product.porciones;
    product.cantidad = product.porciones;
  }
  
  // Compatibilidad de presentación: presentation <-> presentacion
  if (!product.presentation && product.presentacion) {
    product.presentation = product.presentacion;
  }
  if (!product.presentacion && product.presentation) {
    product.presentacion = product.presentation;
  }
  
  // Asegurar que available/disponible sea boolean
  if (product.available === undefined && product.disponible !== undefined) {
    product.available = product.disponible !== false;
  }
  if (product.disponible === undefined && product.available !== undefined) {
    product.disponible = product.available !== false;
  }
  
  return product;
};

const normalizeSupply = (supply) => {
  // Default classification
  supply.classification = supply.classification || "ingrediente";
  return supply;
};

export const createProduct = async (data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  
  // Asegurar que los campos booleanos sean booleanos, no strings
  const payload = {
    ...data,
    available: data.available === true || data.available === "true" || data.disponible === true || data.disponible === "true",
    destacado: data.destacado === true || data.destacado === "true",
    active: data.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  // Ya no se agregan presentacionesPermitidas ni precios automáticamente
  // Solo se guarda priceBase (precioBase)
  
  const docRef = await addDoc(collection(db, "products"), payload);
  return docRef.id;
};

export const listProducts = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
  return snapshot.docs.map((docItem) => {
    const product = {
      id: docItem.id,
      ...docItem.data(),
    };
    return normalizeProduct(product);
  });
};

export const listenProducts = (callback) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const productsQuery = query(collection(db, "products"), orderBy("createdAt", "desc"));
  return onSnapshot(productsQuery, (snapshot) => {
    const products = snapshot.docs.map((docItem) => {
      const product = {
        id: docItem.id,
        ...docItem.data(),
      };
      return normalizeProduct(product);
    });
    callback(products);
  });
};

export const updateProduct = async (productId, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  
  // Asegurar que los campos booleanos sean booleanos, no strings
  const payload = {
    ...data,
  };
  
  // Normalizar campos booleanos
  if (payload.disponible !== undefined) {
    payload.disponible = payload.disponible === true || payload.disponible === "true";
  }
  if (payload.destacado !== undefined) {
    payload.destacado = payload.destacado === true || payload.destacado === "true";
  }
  
  // Siempre actualizar updatedAt cuando se modifica
  payload.updatedAt = serverTimestamp();
  
  await updateDoc(doc(db, "products", productId), payload);
};

// INSUMOS
export const createSupply = async (data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const payload = {
    ...data,
    classification: data.classification || "ingrediente",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "supplies"), payload);
  return docRef.id;
};

export const getSupply = async (supplyId) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDoc(doc(db, "supplies", supplyId));
  if (!snapshot.exists()) return null;
  const supply = { id: snapshot.id, ...snapshot.data() };
  return normalizeSupply(supply);
};

export const listSupplies = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDocs(query(collection(db, "supplies"), orderBy("name", "asc")));
  return snapshot.docs.map((docItem) =>
    normalizeSupply({
      id: docItem.id,
      ...docItem.data(),
    })
  );
};

export const listenSupplies = (callback) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const suppliesQuery = query(collection(db, "supplies"), orderBy("name", "asc"));
  return onSnapshot(suppliesQuery, (snapshot) => {
    const supplies = snapshot.docs.map((docItem) =>
      normalizeSupply({
        id: docItem.id,
        ...docItem.data(),
      })
    );
    callback(supplies);
  });
};

export const updateSupply = async (supplyId, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const payload = {
    ...data,
    updatedAt: serverTimestamp(),
  };
  if (
    payload.classification === undefined ||
    payload.classification === null ||
    payload.classification === ""
  ) {
    delete payload.classification;
  }
  await updateDoc(doc(db, "supplies", supplyId), payload);
};

// RECETAS (relación producto -> insumos)
export const getProductRecipe = async (productId) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDoc(doc(db, "recipes", productId));
  return snapshot.exists() ? snapshot.data() : null;
};

export const setProductRecipe = async (productId, ingredients) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  await setDoc(doc(db, "recipes", productId), {
    productId,
    ingredients,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// MOVIMIENTOS DE STOCK
export const createMovement = async (data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const payload = {
    ...data,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "movements"), payload);
  return docRef.id;
};

export const listMovements = async (supplyId = null, fromDate = null, toDate = null) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  let q = query(collection(db, "movements"), orderBy("createdAt", "desc"));
  
  if (supplyId) {
    q = query(q, where("supplyId", "==", supplyId));
  }
  
  const snapshot = await getDocs(q);
  let movements = snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data(),
  }));
  
  if (fromDate || toDate) {
    movements = movements.filter((mov) => {
      const movDate = mov.createdAt?.toDate ? mov.createdAt.toDate() : new Date(mov.createdAt);
      if (fromDate && movDate < new Date(fromDate)) return false;
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (movDate > to) return false;
      }
      return true;
    });
  }
  
  return movements;
};

// ÓRDENES POR USUARIO (solo pedidos donde userId coincide; requiere índice en Firestore)
export const getUserOrders = async (userId) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  if (!userId) return [];

  try {
    const q = query(
      collection(db, "orders"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));
  } catch (error) {
    console.error("Error obteniendo órdenes del usuario:", error);
    return [];
  }
};

// BANNERS

const normalizeBanner = (banner) => {
  // Ensure dates are Date objects
  if (banner.startDate && typeof banner.startDate.toDate === 'function') {
    banner.startDate = banner.startDate.toDate();
  }
  if (banner.endDate && typeof banner.endDate.toDate === 'function') {
    banner.endDate = banner.endDate.toDate();
  }
  if (banner.createdAt && typeof banner.createdAt.toDate === 'function') {
    banner.createdAt = banner.createdAt.toDate();
  }
  if (banner.updatedAt && typeof banner.updatedAt.toDate === 'function') {
    banner.updatedAt = banner.updatedAt.toDate();
  }
  return banner;
};

export const createBanner = async (data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  
  const payload = {
    ...data,
    active: data.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, "banners"), payload);
  return docRef.id;
};

export const listBanners = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  const snapshot = await getDocs(query(collection(db, "banners"), orderBy("order", "asc")));
  return snapshot.docs.map((docItem) => {
    const banner = {
      id: docItem.id,
      ...docItem.data(),
    };
    return normalizeBanner(banner);
  });
};

export const listenBanners = (callback) => {
  const db = initFirebase();
  if (!db) {
    console.warn("Firebase no disponible para banners");
    return () => {};
  }
  
  try {
    const bannersQuery = query(collection(db, "banners"), orderBy("order", "asc"));
    return onSnapshot(bannersQuery, (snapshot) => {
      const banners = snapshot.docs.map((docItem) => {
        const banner = {
          id: docItem.id,
          ...docItem.data(),
        };
        return normalizeBanner(banner);
      });
      callback(banners);
    }, (error) => {
      console.warn("Error escuchando banners:", error.message);
      // Fallback: intentar fetch único si falla el listener
      if (error.code === 'permission-denied') {
        listBanners().then(banners => callback(banners)).catch(err => {
          console.warn("Error en fallback de banners:", err.message);
          callback([]);
        });
      }
    });
  } catch (error) {
    console.warn("Error configurando listener de banners:", error.message);
    return () => {};
  }
};

export const updateBanner = async (bannerId, data) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  
  const payload = {
    ...data,
  };
  
  // Normalize boolean
  if (payload.active !== undefined) {
    payload.active = payload.active === true || payload.active === "true";
  }
  
  payload.updatedAt = serverTimestamp();
  
  await updateDoc(doc(db, "banners", bannerId), payload);
};

export const deleteBanner = async (bannerId) => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");
  await deleteDoc(doc(db, "banners", bannerId));
};

/** Días de ventana para contar pedidos al recalcular la pizza más vendida (dashboard). */
const TOP_PIZZA_STATS_LOOKBACK_DAYS = 30;

/**
 * Recalcula la pizza más vendida desde pedidos confirmados y pagados y guarda en config/stats.
 * Debe ejecutarse desde el dashboard (usuario con rol admin/gestor).
 */
export const recalculateAndSavePublicTopPizzaStats = async () => {
  const db = initFirebase();
  if (!db) throw new Error("Firebase no disponible.");

  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - TOP_PIZZA_STATS_LOOKBACK_DAYS);

  const orders = await fetchConfirmedPaidOrders({ fromDate });
  const products = await listProducts();
  const { productsMap, productsByName } = buildProductMaps(products);
  const result = computeTopPizzaFromOrders(orders, productsMap, productsByName);

  await setDoc(
    doc(db, "config", "stats"),
    {
      topPizzaProductId: result?.productId ?? null,
      topPizzaName: result?.name ?? null,
      topPizzaImageUrl: result?.imageUrl ?? null,
      topPizzaOrderCount: result?.count ?? null,
      topPizzaLastUpdatedAt: serverTimestamp(),
      topPizzaStatsPeriodDays: TOP_PIZZA_STATS_LOOKBACK_DAYS,
      topPizzaStatsSource: "manual_dashboard",
    },
    { merge: true }
  );

  return result;
};
